import {
  listKontaktKunden,
  listKontaktLieferanten,
  type GeschaeftspartnerListResponse,
  type GeschaeftspartnerTyp,
  type ListGeschaeftspartnerParams,
} from "@/lib/kontakte";
import type { ListViewStorage } from "@/pages/kontakte/geschaeftspartnerListView";

const STORAGE_PREFIX = "eh.kontakte.listpayload.";

const memory = new Map<string, GeschaeftspartnerListResponse>();
const inflight = new Map<string, Promise<GeschaeftspartnerListResponse>>();

export function geschaeftspartnerListCacheId(
  typ: GeschaeftspartnerTyp,
  params: ListGeschaeftspartnerParams,
): string {
  return [
    typ,
    (params.q ?? "").trim(),
    String(params.offset ?? 0),
    String(params.limit ?? 0),
    params.sort ?? "",
    params.direction ?? "",
  ].join("\u0001");
}

function browserStorage(): ListViewStorage | null {
  try {
    if (typeof sessionStorage === "undefined") return null;
    return sessionStorage;
  } catch {
    return null;
  }
}

function isListResponse(value: unknown): value is GeschaeftspartnerListResponse {
  if (!value || typeof value !== "object") return false;
  const row = value as GeschaeftspartnerListResponse;
  return Array.isArray(row.items) && typeof row.total === "number";
}

export function readGeschaeftspartnerListCache(
  typ: GeschaeftspartnerTyp,
  params: ListGeschaeftspartnerParams,
  storage: ListViewStorage | null = browserStorage(),
): GeschaeftspartnerListResponse | null {
  const id = geschaeftspartnerListCacheId(typ, params);
  const cached = memory.get(id);
  if (cached) return cached;
  if (!storage) return null;
  try {
    const raw = storage.getItem(`${STORAGE_PREFIX}${typ}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: unknown; data?: unknown };
    if (parsed.id !== id || !isListResponse(parsed.data)) return null;
    memory.set(id, parsed.data);
    return parsed.data;
  } catch {
    return null;
  }
}

export function rememberGeschaeftspartnerList(
  typ: GeschaeftspartnerTyp,
  params: ListGeschaeftspartnerParams,
  data: GeschaeftspartnerListResponse,
  storage: ListViewStorage | null = browserStorage(),
): void {
  const id = geschaeftspartnerListCacheId(typ, params);
  memory.set(id, data);
  if (!storage) return;
  try {
    storage.setItem(`${STORAGE_PREFIX}${typ}`, JSON.stringify({ id, data }));
  } catch {
    // sessionStorage kann voll oder blockiert sein; der Speicher im Tab bleibt.
  }
}

/** Nach dem Löschen die gemerkte Liste verwerfen, damit der Eintrag nicht kurz wieder auftaucht. */
export function forgetGeschaeftspartnerList(
  typ: GeschaeftspartnerTyp,
  storage: ListViewStorage | null = browserStorage(),
): void {
  const prefix = `${typ}\u0001`;
  for (const key of [...memory.keys()]) {
    if (key.startsWith(prefix)) memory.delete(key);
  }
  storage?.removeItem(`${STORAGE_PREFIX}${typ}`);
}

function listForTyp(
  typ: GeschaeftspartnerTyp,
  params: ListGeschaeftspartnerParams,
): Promise<GeschaeftspartnerListResponse> {
  return typ === "kunde" ? listKontaktKunden(params) : listKontaktLieferanten(params);
}

/** Eine laufende Abfrage derselben Liste teilen, Ergebnis für den nächsten Besuch merken. */
export function loadGeschaeftspartnerList(
  typ: GeschaeftspartnerTyp,
  params: ListGeschaeftspartnerParams,
): Promise<GeschaeftspartnerListResponse> {
  const id = geschaeftspartnerListCacheId(typ, params);
  const pending = inflight.get(id);
  if (pending) return pending;
  const request = listForTyp(typ, params)
    .then((data) => {
      rememberGeschaeftspartnerList(typ, params, data);
      return data;
    })
    .finally(() => {
      if (inflight.get(id) === request) inflight.delete(id);
    });
  inflight.set(id, request);
  return request;
}
